import { gymLevel, runTrack } from './gym';
import { castleKeep, desertStack, factoryLine, woodsClearing, woodsRidge } from './handauthored';
import { GENERATED_LEVELS } from './generated';
import { LARGE_ARENAS } from './large';
import { OFFICE_LEVEL_ID, officeOvertime } from './office';
import { PIRATE_ARENAS } from './pirate';
import { NEON_ARENAS } from './neon';
import type { LevelDef } from '../sim/level/schema';
import { levelCapacity, parseLevel } from '../sim/level/schema';
import { MAX_PLAYERS } from '../core/players';

export const HAND_AUTHORED: LevelDef[] = [
  woodsClearing,
  woodsRidge,
  desertStack,
  factoryLine,
  castleKeep,
];

function uniquify(levels: LevelDef[]): LevelDef[] {
  const used = new Set<string>();
  return levels.map((level) => {
    if (!used.has(level.id)) {
      used.add(level.id);
      return parseLevel(level);
    }
    const slug = `${level.theme}-${level.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`.replace(
      /-+$/g,
      '',
    );
    let id = slug;
    let n = 2;
    while (used.has(id)) {
      id = `${slug}-${n}`;
      n += 1;
    }
    used.add(id);
    return parseLevel({ ...level, id });
  });
}

export const ALL_LEVELS: LevelDef[] = uniquify([
  gymLevel,
  runTrack,
  ...HAND_AUTHORED,
  ...GENERATED_LEVELS,
  ...LARGE_ARENAS,
  officeOvertime,
  ...PIRATE_ARENAS,
  ...NEON_ARENAS,
]);

const byId = new Map(ALL_LEVELS.map((l) => [l.id, l]));

export function getLevel(id: string): LevelDef {
  const level = byId.get(id);
  if (!level) throw new Error(`Unknown level ${id}`);
  return level;
}

export function levelsForThemes(themes: string[]): LevelDef[] {
  return ALL_LEVELS.filter((l) => themes.includes(l.theme) && isMatchLevel(l));
}

export function isMatchLevel(level: LevelDef): boolean {
  return !level.id.startsWith('test-') && level.id !== 'gym' && level.id !== 'run-track';
}

export function builtInMatchLevels(): LevelDef[] {
  return ALL_LEVELS.filter(isMatchLevel);
}

/**
 * Keep the original default pool for 2–4 fighters. Larger parties need enough authored starts;
 * if host toggles leave none, use the large built-ins rather than stacking players on small maps.
 * Explicitly chosen large arenas can still host a smaller party.
 */
export function matchLevelPool(
  enabled: string[] | 'all',
  extra: LevelDef[] = [],
  players = 4,
  mode = 'classic',
): LevelDef[] {
  // Office equipment and the authored office belong together, even with old arena toggles.
  if (mode === 'office-brawl') return [getLevel(OFFICE_LEVEL_ID)];
  const count = Number.isFinite(players)
    ? Math.max(1, Math.min(MAX_PLAYERS, Math.floor(players)))
    : 4;
  const seen = new Set<string>();
  const out: LevelDef[] = [];
  for (const level of [...builtInMatchLevels(), ...extra]) {
    if (seen.has(level.id)) continue;
    if (enabled === 'all' && level.id === OFFICE_LEVEL_ID) continue;
    if (enabled !== 'all' && !enabled.includes(level.id)) continue;
    const capacity = levelCapacity(level);
    if (capacity < count) continue;
    if (count <= 4 && enabled === 'all' && capacity > 4) continue;
    seen.add(level.id);
    out.push(level);
  }
  if (out.length === 0 && count > 4)
    return builtInMatchLevels().filter((level) => level.id !== OFFICE_LEVEL_ID && levelCapacity(level) >= count);
  return out;
}

export function hazardTestLevels(): LevelDef[] {
  return ALL_LEVELS.filter((l) => l.id.startsWith('test-'));
}

export { gymLevel, runTrack, levelCapacity, LARGE_ARENAS, OFFICE_LEVEL_ID, officeOvertime, PIRATE_ARENAS, NEON_ARENAS };
